import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ClipboardList, Loader2, ChevronDown, ChevronUp, MapPin, MessageSquare, AlertCircle } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { apiFetch } from '../lib/api';

type Mode = 'order' | 'delivery';

interface SupplierOrderItem {
  productId: number;
  name: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
}

interface SupplierOrder {
  id: number;
  orderNum: string;
  customerName: string;
  customerPhone: string;
  address: string;
  estate: string;
  deliveryDate: string;
  deliveryTime: string;
  remarks: string;
  items: SupplierOrderItem[];
  createdAt: number;
  paymentConfirmed: number;
  orderCompleted: number;
}

// 取香港今日日期 (UTC+8)
function hkToday(): string {
  return new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);
}

// 模式 → i18n key（落單日/送貨日）
const MODE_LABELS: Record<Mode, string> = {
  order: 'partner:supplier.modeOrder',
  delivery: 'partner:supplier.modeDelivery',
};

const SupplierOrders: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  // 日期格式跟隨介面語言（zh-HK / en-GB）
  const dateLocale = i18n.language?.toLowerCase().startsWith('en') ? 'en-GB' : 'zh-HK';
  const token = localStorage.getItem('admin_token');

  const [date, setDate] = useState<string>(hkToday());
  const [mode, setMode] = useState<Mode>('delivery');
  const [orders, setOrders] = useState<SupplierOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  }, [navigate]);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/public/supplier/orders?date=${date}&mode=${mode}`);
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const json = await res.json().catch(() => ([]));
      if (!res.ok) throw new Error((json as any).error || t('partner:supplier.orders.loadFailed'));
      setOrders(Array.isArray(json) ? (json as SupplierOrder[]) : []);
    } catch (e: any) {
      setError(e.message || t('partner:supplier.orders.loadFailed'));
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [date, mode, handleUnauthorized]);

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    loadOrders();
  }, [token, navigate, loadOrders]);

  const toggleExpand = (id: number) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <AdminLayout currentPage="supplier-orders" onLogout={handleUnauthorized}>
      <div className="p-4 md:p-8">
        {/* 頁首 */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-orange-600" />
            {t('partner:supplier.orders.title')}
          </h1>
          <LanguageSwitcher size="sm" />
        </div>

        {/* 常駐警示條 */}
        <div className="mb-6 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-sm">
          {t('partner:supplier.orders.privacyNotice')}
        </div>

        {/* 控制列 */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
            {(Object.keys(MODE_LABELS) as Mode[]).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  mode === m
                    ? 'bg-orange-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {t(MODE_LABELS[m])}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-600 dark:text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            {t('partner:supplier.loading')}
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 px-6 py-16 text-center text-gray-600 dark:text-gray-400">
            {t('partner:supplier.orders.empty')}
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const isOpen = !!expanded[order.id];
              const completed = order.orderCompleted === 1;
              return (
                <div
                  key={order.id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden"
                >
                  {/* 卡片摘要 */}
                  <div className="p-4 md:p-6 flex flex-wrap items-center gap-4">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-lg font-bold text-gray-800 dark:text-white">{order.orderNum}</span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            completed
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          }`}
                        >
                          {completed ? t('partner:supplier.orders.completed') : t('partner:supplier.orders.pending')}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        {t('partner:supplier.orders.customerWithPhone', { name: order.customerName, phone: order.customerPhone })}
                      </div>
                      <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        {t('partner:supplier.orders.deliveryAt')}{order.deliveryDate} {order.deliveryTime}
                        {order.estate && <span className="ml-3">{t('partner:supplier.orders.estateLabel')}{order.estate}</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => toggleExpand(order.id)}
                      className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-orange-600 border border-orange-600 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      {isOpen ? (
                        <>
                          {t('partner:supplier.orders.collapse')} <ChevronUp className="w-4 h-4" />
                        </>
                      ) : (
                        <>
                          {t('partner:supplier.orders.expand')} <ChevronDown className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>

                  {/* 展開詳情 */}
                  {isOpen && (
                    <div className="border-t border-gray-200 dark:border-gray-700 p-4 md:p-6 space-y-4 bg-gray-50 dark:bg-gray-900/40">
                      {/* 產品明細 */}
                      <div>
                        <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-2">{t('partner:supplier.orders.yourItems')}</h3>
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
                          {order.items.map((item) => (
                            <div key={item.productId} className="px-4 py-3 flex items-center justify-between gap-4">
                              <span className="text-gray-800 dark:text-white">
                                {item.name} <span className="text-gray-600 dark:text-gray-400">x {item.quantity}</span>
                              </span>
                              <span className="text-gray-800 dark:text-white font-medium whitespace-nowrap">
                                HK${item.subtotal.toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* 送貨地址 */}
                      <div className="flex items-start gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-gray-600 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-semibold text-gray-800 dark:text-white">{t('partner:supplier.orders.addressLabel')}</span>
                          <span className="text-gray-600 dark:text-gray-400">{order.address || '—'}</span>
                        </div>
                      </div>

                      {/* 客戶備註 */}
                      <div className="flex items-start gap-2 text-sm">
                        <MessageSquare className="w-4 h-4 text-gray-600 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-semibold text-gray-800 dark:text-white">{t('partner:supplier.orders.remarksLabel')}</span>
                          <span className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                            {order.remarks || '—'}
                          </span>
                        </div>
                      </div>

                      {/* 落單時間 */}
                      {order.createdAt > 0 && (
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          {t('partner:supplier.orders.orderedAt')}{new Date(order.createdAt * 1000).toLocaleString(dateLocale)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default SupplierOrders;
